import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Mail, 
  Handshake, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const username = localStorage.getItem('adminUsername');

  useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.clear();
        navigate('/admin/login');
        toast.error('Session expired. Please login again.');
      }
    };

    verifyToken();
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/contacts', icon: Mail, label: 'Contact Inquiries' },
    { path: '/admin/dealers', icon: Handshake, label: 'Dealer Applications' },
    { path: '/admin/catalog', icon: FileText, label: 'Catalog' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar - Desktop */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} hidden lg:block bg-zinc-900 text-white transition-all duration-300 fixed h-full`}>
        <div className="p-4">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            {isSidebarOpen && (
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_rk-rotoarc-preview/artifacts/qs3pkdc4_IMG_4982.jpeg" 
                  alt="RK ROTOARC" 
                  className="h-10 brightness-0 invert"
                />
                <p className="text-xs text-zinc-400 mt-2">Admin Panel</p>
              </div>
            )}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'text-zinc-300 hover:bg-zinc-800'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {isSidebarOpen && <span>{item.label}</span>}
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
            {isSidebarOpen && (
              <div className="mb-3">
                <p className="text-sm font-semibold text-white">{username}</p>
                <p className="text-xs text-zinc-400">Administrator</p>
              </div>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-zinc-300 hover:text-white hover:bg-zinc-800 justify-start"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {isSidebarOpen && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <aside className="w-64 bg-zinc-900 text-white h-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rk-rotoarc-preview/artifacts/qs3pkdc4_IMG_4982.jpeg" 
                  alt="RK ROTOARC" 
                  className="h-10 brightness-0 invert"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <nav className="space-y-2">
                {menuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-yellow-500 text-black font-semibold'
                        : 'text-zinc-300 hover:bg-zinc-800'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>

              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-white">{username}</p>
                  <p className="text-xs text-zinc-400">Administrator</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="w-full text-zinc-300 hover:text-white hover:bg-zinc-800 justify-start"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="ml-3">Logout</span>
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} transition-all duration-300`}>
        {/* Top Bar - Mobile */}
        <div className="lg:hidden bg-white border-b border-zinc-200 p-4 flex items-center justify-between sticky top-0 z-40">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 hover:bg-zinc-100 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img 
            src="https://customer-assets.emergentagent.com/job_rk-rotoarc-preview/artifacts/qs3pkdc4_IMG_4982.jpeg" 
            alt="RK ROTOARC" 
            className="h-8"
          />
          <div className="w-10"></div>
        </div>

        {/* Page Content */}
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
